// utils/GenerateOpenRoomIssuesPDF.js
import jsPDF from "jspdf";
import {
  query,
  getDocs,
  orderBy,
  limit,
  where,
} from "firebase/firestore";
import { propertyCollection } from "../../utils/firestorePaths";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";

const GenerateOpenRoomIssuesPDF = async (
  clientId,
  selectedProperty,
  rooms,
  setStatusMessage,
  user,
  fileName = "Open Room Issues"
) => {
  setStatusMessage("Initializing PDF");
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: [280, 297], // keep your widened page
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 5;
  const maxLineWidth = pageWidth - margin * 2;

  const leftAlign = (text, y) => {
    const wrapped = pdf.splitTextToSize(text, maxLineWidth);
    pdf.text(wrapped, margin, y);
  };
  const centerAlign = (text, y) => {
    const w = pdf.getTextWidth(text);
    const x = (pageWidth - w) / 2;
    pdf.text(text, x, y);
  };

  // ---------- prefetch open issues per room (so cover page can summarize) ----------
  setStatusMessage("Scanning rooms for open issues…");
  const openIssuesByRoomId = new Map(); // room.id -> {room, issues: [docs]}
  let totalOpenIssues = 0;

  for (const room of rooms) {
    const issuesRef = propertyCollection(
      clientId,
      selectedProperty,
      "units",
      room.id,
      "unitIssues"
    );
    const issuesSnap = await getDocs(
      query(issuesRef, where("issueStatus", "==", "open"))
    );
    if (!issuesSnap.empty) {
      const issues = issuesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      openIssuesByRoomId.set(room.id, { room, issues });
      totalOpenIssues += issues.length;
    }
  }

  // ---------- cover page ----------
  const contentTop = drawReportHeader(pdf, {
    title: fileName || "Open Room Issues",
    generatedBy: user?.fullName ?? "Unknown",
  });

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(12);
  centerAlign(`Rooms with open issues`, contentTop + 6);

  const roomsWithIssues = Array.from(openIssuesByRoomId.values()).map(
    ({ room }) => room.displayName || room.room || room.id
  );

  leftAlign(
    roomsWithIssues.length
      ? roomsWithIssues.join(", ")
      : "No rooms currently have open issues.",
    contentTop + 12
  );

  centerAlign(`Total open issues: ${totalOpenIssues}`, 100);

  // end cover
  pdf.addPage();

  // ---------- helpers ----------
  const loadImage = (url) =>
    new Promise(async (resolve, reject) => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => resolve({ img, objUrl });
        img.onerror = () => reject(new Error("Image decode failed"));
        img.src = objUrl;
      } catch (err) {
        reject(err);
      }
    });

  const placeGrid = (pdfInstance, items, startY = 50, makeCell) => {
    // items: array of arbitrary data; makeCell(i, x, y) draws it
    const startX = 10;
    const tileW = 80;
    const tileH = 80;
    const gap = 90;
    const perRow = 3;

    for (let i = 0; i < items.length; i++) {
      const col = i % perRow;
      const row = Math.floor(i / perRow);
      const x = startX + col * gap;
      const y = startY + row * gap;
      makeCell(items[i], i, x, y, tileW, tileH);
    }
  };

  // ---------- iterate ALL rooms; first page like before (images + items), second page = open issues ----------
  const roomsArray = rooms; // no filtering

  setStatusMessage(`Generating PDF for ${roomsArray.length} rooms`);

  // For the front page summary later (optional): compute itemsNeeded across all rooms
  let itemsNeeded = [];

  for (let rIndex = 0; rIndex < roomsArray.length; rIndex++) {
    const room = roomsArray[rIndex];

    // ---------- PAGE 1: Room overview (images & items needed like your current report) ----------
    pdf.setFontSize(20);
    pdf.setFont("helvetica", "bold");
    pdf.text(room.displayName || room.room || room.id, 10, 20);

    if (room.unitNotes) {
      pdf.setFontSize(10);
      leftAlign(`Notes: ${room.unitNotes}`, 30);
    }

    // Items needed (from itemCount* keys)
    const unitSpecificItemsNeeded = [];
    Object.keys(room).forEach((key) => {
      if (key.startsWith("itemCount") && room[key] > 0) {
        const itemName = key.replace("itemCount", "");
        const existing = itemsNeeded.find((i) => i.item === itemName);
        if (existing) existing.count += room[key];
        else itemsNeeded.push({ item: itemName, count: room[key] });
        unitSpecificItemsNeeded.push({ item: itemName, count: room[key] });
      }
    });

    if (unitSpecificItemsNeeded.length) {
      pdf.setFontSize(10);
      leftAlign(
        `Items Needed: ${unitSpecificItemsNeeded
          .sort((a, b) => a.item.localeCompare(b.item))
          .map((i) => `${i.item}: ${i.count}`)
          .join(", ")}`,
        43
      );
    }

    // Room images (latest 6, parallel)
    setStatusMessage(`Fetching images for ${room.displayName}`);
    try {
      const imagesRef = propertyCollection(
        clientId,
        selectedProperty,
        "units",
        room.id,
        "images"
      );
      const snap = await getDocs(
        query(imagesRef, orderBy("uploadedAt", "desc"), limit(6))
      );
      const docs = snap.docs.slice(0, 6);

      const loaders = docs.map((doc, idx) =>
        loadImage(doc.data().url)
          .then(({ img, objUrl }) => ({
            status: "fulfilled",
            idx,
            img,
            objUrl,
            url: doc.data().url,
          }))
          .catch((error) => ({ status: "rejected", idx, error }))
      );

      const results = await Promise.allSettled(loaders);

      // draw grid
      placeGrid(pdf, results, 50, (res, i, x, y, w, h) => {
        const r = res.status === "fulfilled" ? res.value : res.reason || res;
        if (r?.img) {
          try {
            pdf.addImage(r.img, "JPEG", x, y, w, h);
            if (r.url) pdf.link(x, y, w, h, { url: r.url });
          } catch {
            pdf.setFontSize(12);
            pdf.setFont("helvetica", "normal");
            pdf.text("Issue adding image", x, y + h / 2);
          } finally {
            if (r.objUrl) URL.revokeObjectURL(r.objUrl);
          }
        } else {
          pdf.setFontSize(12);
          pdf.setFont("helvetica", "normal");
          pdf.text("Issue getting image", x, y + h / 2);
        }
      });
    } catch {
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      leftAlign("Issue fetching room images.", 140);
    }

    // ---------- PAGE 2: Open Issues for this room ----------
    pdf.addPage();

    pdf.setFontSize(18);
    pdf.setFont("helvetica", "bold");
    pdf.text(
      `${room.displayName || room.room || room.id} — Open Issues`,
      10,
      20
    );

    const openSet = openIssuesByRoomId.get(room.id);
    if (!openSet) {
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      leftAlign("No open issues found for this room.", 35);
    } else {
      const issues = openSet.issues;

      // Prepare lightweight render items for grid (cap to 9 to keep to “second page”)
      const capped = issues.slice(0, 9);
      const cells = await Promise.all(
        capped.map(async (iss) => {
          let imgData = null;
          if (iss.issueImage) {
            try {
              imgData = await loadImage(iss.issueImage);
            } catch {
              imgData = null;
            }
          }
          const reportedDate = (() => {
            const v = iss.issueReportedAt;
            const d =
              v?.toDate?.() instanceof Date
                ? v.toDate()
                : v instanceof Date
                ? v
                : new Date(v);
            return isNaN(d) ? "Unknown date" : d.toLocaleString();
          })();

          return {
            img: imgData?.img || null,
            objUrl: imgData?.objUrl || null,
            url: iss.issueImage || null,
            label: `Reported: ${reportedDate}`,
          };
        })
      );

      // draw grid of issues (3x3)
      placeGrid(pdf, cells, 35, (cell, i, x, y, w, h) => {
        if (cell.img) {
          try {
            pdf.addImage(cell.img, "JPEG", x, y, w, h);
            if (cell.url) pdf.link(x, y, w, h, { url: cell.url });
          } catch {
            pdf.setFontSize(10);
            pdf.text("Issue adding image", x, y + h / 2);
          } finally {
            if (cell.objUrl) URL.revokeObjectURL(cell.objUrl);
          }
        } else {
          pdf.setFontSize(10);
          pdf.text("No image", x + 2, y + 6);
        }
        // caption
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "normal");
        const caption = pdf.splitTextToSize(cell.label, w);
        pdf.text(caption, x, y + h + 5);
      });

      if (issues.length > 9) {
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "italic");
        leftAlign(`+${issues.length - 9} more open issue(s) not shown.`, 285);
      }
    }

    // add a new page unless this was the last room
    if (rIndex !== roomsArray.length - 1) {
      pdf.addPage();
    }
  }

  // Optional: write global itemsNeeded on the cover (page 1)
  pdf.setPage(1);
  if (itemsNeeded.length) {
    pdf.setFontSize(12);
    leftAlign(
      itemsNeeded
        .sort((a, b) => a.item.localeCompare(b.item))
        .map((i) => `${i.item}: ${i.count}`)
        .join(", "),
      125
    );
  }

  setStatusMessage("Finishing up PDF");

  stampPageNumbers(pdf);
  const blob = pdf.output("blob");
  downloadBlob(blob, buildReportFilename("Open_Room_Issues", { ext: "pdf" }));
};

export default GenerateOpenRoomIssuesPDF;
